import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { meApi } from '../services/api';
import { usePlatformEvents } from '../contexts/PlatformEventContext';
import type { PlatformEvent } from '../types/events';
import type {
  ActivityItem,
  OverviewResponse,
  OverviewIncludeKey,
  RunningAgent,
} from '../types';

const STALE_MS = 30_000;
const RECENT_ACTIVITIES_CAP = 10;
// Keep disconnected users visible (status=away) for this long before removing.
const PRESENCE_LINGER_MS = 60_000;
// Wait this long after WS reconnect before re-syncing /overview, so the
// connect-Lambda has time to write the presence row in DDB. Without this
// the refetch races ahead of the write and active users can briefly read 0.
const RECONNECT_REFETCH_DELAY_MS = 2_000;

interface UseOverviewOptions {
  include?: OverviewIncludeKey[];
}

const QUERY_KEY = (include?: OverviewIncludeKey[]) =>
  ['me', 'overview', include ? [...include].sort().join(',') : 'all'] as const;

function clampMin(n: number | undefined, min = 0): number {
  return Math.max(min, n ?? 0);
}

function bumpStat(
  prev: OverviewResponse | undefined,
  key: keyof OverviewResponse['stats'],
  delta: number,
): OverviewResponse | undefined {
  if (!prev) return prev;
  const current = prev.stats[key];
  if (current === undefined) return prev;
  return {
    ...prev,
    stats: { ...prev.stats, [key]: clampMin(current + delta) },
  };
}

function eventToActivity(event: PlatformEvent): ActivityItem {
  const d = event.data as Record<string, unknown>;
  const activityId = (d.activityId as string) || event.subjectId || event.eventId;
  return {
    activityId,
    orgId: event.orgId,
    spaceId: (d.spaceId as string) ?? '',
    userId: d.userId as string | undefined,
    source: ((d.activitySource ?? d.source ?? 'system') as ActivityItem['source']),
    triggerSource: d.triggerSource as ActivityItem['triggerSource'],
    category: (d.category as string) ?? '',
    priority: (d.priority as ActivityItem['priority']) ?? 'normal',
    visibility: (d.visibility as ActivityItem['visibility']) ?? 'org',
    title: (d.title as string) ?? '',
    summary: d.summary as string | undefined,
    relatedEntityIds: d.relatedEntityIds as ActivityItem['relatedEntityIds'],
    metadata: d.metadata as Record<string, unknown> | undefined,
    createdAt: event.metadata?.timestamp ?? new Date().toISOString(),
  };
}

export function useOverview(options?: UseOverviewOptions) {
  const include = options?.include;
  const queryClient = useQueryClient();
  const { subscribe, isConnected, diagnostics } = usePlatformEvents();
  const lastSyncRef = useRef<number>(0);
  // Pending removal timers per offline userId. We linger users marked `away`
  // before actually dropping them so brief disconnects don't blink off.
  const offlineTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  const queryKey = QUERY_KEY(include);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const result = await meApi.overview(include);
      lastSyncRef.current = Date.now();
      return result;
    },
    staleTime: STALE_MS,
  });

  const patchCache = useCallback(
    (updater: (prev: OverviewResponse | undefined) => OverviewResponse | undefined) => {
      queryClient.setQueryData<OverviewResponse>(queryKey, (prev) => updater(prev) ?? prev);
    },
    [queryClient, queryKey],
  );

  const handleEvent = useCallback(
    (event: PlatformEvent) => {
      const type = event.eventType;
      const data = event.data as Record<string, unknown>;

      if (type === 'activity.created') {
        const activity = eventToActivity(event);
        patchCache((prev) => {
          if (!prev) return prev;
          const next = { ...prev, stats: { ...prev.stats }, lists: { ...prev.lists } };
          if (next.stats.todayActivities !== undefined) {
            next.stats.todayActivities = clampMin(next.stats.todayActivities + 1);
          }
          if (next.lists.recentActivities) {
            const existingIds = new Set(next.lists.recentActivities.map((a) => a.activityId));
            if (!existingIds.has(activity.activityId)) {
              next.lists.recentActivities = [activity, ...next.lists.recentActivities].slice(
                0,
                RECENT_ACTIVITIES_CAP,
              );
            }
          }
          return next;
        });
        return;
      }

      if (type === 'agent.run.started') {
        const runId = (data.runId as string) || event.subjectId;
        if (!runId) return;
        const newRun: RunningAgent = {
          runId,
          agentId: (data.agentId as string) ?? '',
          agentName: data.agentName as string | undefined,
          conversationId: data.conversationId as string | undefined,
          startedAt: event.metadata?.timestamp ?? new Date().toISOString(),
          status: 'running',
        };
        patchCache((prev) => {
          if (!prev) return prev;
          const next = { ...prev, stats: { ...prev.stats }, lists: { ...prev.lists } };
          if (next.stats.todayAgentRuns !== undefined) {
            next.stats.todayAgentRuns = clampMin(next.stats.todayAgentRuns + 1);
          }
          if (next.lists.runningAgents) {
            const existing = new Set(next.lists.runningAgents.map((r) => r.runId));
            if (!existing.has(newRun.runId)) {
              next.lists.runningAgents = [newRun, ...next.lists.runningAgents];
            }
          }
          return next;
        });
        return;
      }

      if (
        type === 'agent.run.completed' ||
        type === 'agent.run.failed' ||
        type === 'agent.run.cancelled'
      ) {
        const runId = (data.runId as string) || event.subjectId;
        if (!runId) return;
        patchCache((prev) => {
          if (!prev?.lists.runningAgents) return prev;
          return {
            ...prev,
            lists: {
              ...prev.lists,
              runningAgents: prev.lists.runningAgents.filter((r) => r.runId !== runId),
            },
          };
        });
        return;
      }

      // Integration events arrive as provider-prefixed CloudEvents
      // (e.g. `com.atlassian.jira.issue.created`) with
      // `source: 'optr.conn.integrations'`. Match by source, not eventType.
      if (event.source === 'optr.conn.integrations') {
        patchCache((prev) => bumpStat(prev, 'todayIntegrationEvents', 1));
        return;
      }

      if (type === 'kb.document.added') {
        patchCache((prev) => bumpStat(prev, 'totalKbDocuments', 1));
        return;
      }

      if (type === 'presence.user.online') {
        const userId = (data.userId as string) || event.subjectId;
        if (!userId) return;
        // If the user was lingering as `away`, cancel the pending removal.
        const pending = offlineTimersRef.current.get(userId);
        if (pending) {
          clearTimeout(pending);
          offlineTimersRef.current.delete(userId);
        }
        patchCache((prev) => {
          if (!prev?.lists.activeUsers) return prev;
          const existing = prev.lists.activeUsers.users.find((u) => u.userId === userId);
          if (existing) {
            // Already in list (likely lingering as away) — flip back to online
            // and clear the disconnect stamp so the row stops showing "disconnected".
            return {
              ...prev,
              lists: {
                ...prev.lists,
                activeUsers: {
                  ...prev.lists.activeUsers,
                  users: prev.lists.activeUsers.users.map((u) =>
                    u.userId === userId
                      ? { ...u, status: 'online' as const, disconnectedAt: undefined }
                      : u,
                  ),
                },
              },
            };
          }
          const newUser = {
            userId,
            firstName: data.firstName as string | undefined,
            lastName: data.lastName as string | undefined,
            email: data.email as string | undefined,
            avatarUrl: (data.avatarUrl as string | null | undefined) ?? null,
            status: 'online' as const,
            connectedSince:
              (data.connectedSince as string | undefined) ??
              event.metadata?.timestamp,
          };
          return {
            ...prev,
            lists: {
              ...prev.lists,
              activeUsers: {
                users: [...prev.lists.activeUsers.users, newUser],
                count: prev.lists.activeUsers.count + 1,
              },
            },
          };
        });
        return;
      }

      if (type === 'presence.user.offline') {
        const userId = (data.userId as string) || event.subjectId;
        if (!userId) return;
        const disconnectedAt = event.metadata?.timestamp ?? new Date().toISOString();
        // Mark as away immediately; schedule actual removal after the linger.
        patchCache((prev) => {
          if (!prev?.lists.activeUsers) return prev;
          const wasPresent = prev.lists.activeUsers.users.some((u) => u.userId === userId);
          if (!wasPresent) return prev;
          return {
            ...prev,
            lists: {
              ...prev.lists,
              activeUsers: {
                ...prev.lists.activeUsers,
                users: prev.lists.activeUsers.users.map((u) =>
                  u.userId === userId
                    ? { ...u, status: 'away' as const, disconnectedAt }
                    : u,
                ),
              },
            },
          };
        });
        const existingTimer = offlineTimersRef.current.get(userId);
        if (existingTimer) clearTimeout(existingTimer);
        const timer = setTimeout(() => {
          offlineTimersRef.current.delete(userId);
          patchCache((prev) => {
            if (!prev?.lists.activeUsers) return prev;
            const wasPresent = prev.lists.activeUsers.users.some(
              (u) => u.userId === userId,
            );
            if (!wasPresent) return prev;
            return {
              ...prev,
              lists: {
                ...prev.lists,
                activeUsers: {
                  users: prev.lists.activeUsers.users.filter(
                    (u) => u.userId !== userId,
                  ),
                  count: clampMin(prev.lists.activeUsers.count - 1),
                },
              },
            };
          });
        }, PRESENCE_LINGER_MS);
        offlineTimersRef.current.set(userId, timer);
        return;
      }

      if (type === 'inbox.item_added' || type === 'inbox.item.added') {
        patchCache((prev) => bumpStat(prev, 'inboxUnread', 1));
        return;
      }

      if (
        type === 'inbox.item_resolved' ||
        type === 'inbox.item_cancelled' ||
        type === 'inbox.item_expired' ||
        // Transitional aliases — the backend canonical form uses underscores
        // but a straggler producer using dots shouldn't leak into the counter.
        type === 'inbox.item.resolved' ||
        type === 'inbox.item.cancelled' ||
        type === 'inbox.item.expired'
      ) {
        patchCache((prev) => bumpStat(prev, 'inboxUnread', -1));
        return;
      }
    },
    [patchCache],
  );

  useEffect(() => {
    const unsub = subscribe(
      [
        { eventType: 'activity.created' },
        // Overview only counts running agents — lifecycle tier is enough; drop
        // the high-frequency step/delta stream.
        { eventType: 'agent.run.*', level: 'lifecycle' },
        { source: 'optr.conn.integrations' },
        { eventType: 'kb.document.*' },
        { eventType: 'presence.user.*' },
        { eventType: 'inbox.*' },
      ],
      handleEvent,
    );
    return unsub;
  }, [subscribe, handleEvent]);

  useEffect(() => {
    const timers = offlineTimersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  // Refetch when the tab regains focus and the snapshot is older than STALE_MS.
  // The project intentionally disables React Query's global refetchOnWindowFocus
  // (src/main.tsx), so we wire the visibility listener locally.
  const refetch = query.refetch;
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastSyncRef.current < STALE_MS) return;
      refetch();
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refetch]);

  // Re-sync the snapshot after a WS reconnect — events that fired during the
  // disconnect window are gone, so the cache may have drifted (counters,
  // running agents, presence). Skip the initial false→true on first connect
  // (queryFn already runs on mount).
  const prevConnectedRef = useRef(isConnected);
  useEffect(() => {
    const wasConnected = prevConnectedRef.current;
    prevConnectedRef.current = isConnected;
    if (!wasConnected && isConnected && lastSyncRef.current > 0) {
      const t = setTimeout(() => refetch(), RECONNECT_REFETCH_DELAY_MS);
      return () => clearTimeout(t);
    }
  }, [isConnected, refetch]);

  return {
    overview: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    isConnected,
    diagnostics,
  };
}
